// AI 响应窗口决策
// 规则（见开发计划）：
//   致死 → 优先 无敌 / 闪避(w,hw) / 虚灵(f,sf)；反射仅在可反杀时
//   显著伤害 → 反射；小额 → pass 保留手牌
//   缴械/掠夺好装备 → 解除；队友只在目标会致死时出手
import type { GameState, PendingEffect } from "@/types/game";
import type { DeckCard, ResponseKind } from "@/types/cards";
import type { EngineAction } from "@/engine/GameEngine";
import { findSubstituteConfig } from "@/engine/GameEngine";
import { computeDamage } from "@/engine/damage";
import { primaryTarget, canRespondWith } from "@/engine/Resolver";

interface Threat {
  amount: number; // 预计最终伤害/损失
  lethal: boolean; // 会令目标进入濒死
  significant: boolean; // 值得花牌响应
  reflectedWouldKillSource: boolean; // 反射后反杀来源
}

// 估计某效果对 targetId 的威胁程度
export function estimateThreat(
  state: GameState,
  effect: PendingEffect,
  targetId: string
): Threat {
  const target = state.players.find((p) => p.id === targetId)!;
  const source = state.players.find((p) => p.id === effect.source);
  const type = effect.damageType;
  const noThreat: Threat = {
    amount: 0,
    lethal: false,
    significant: false,
    reflectedWouldKillSource: false,
  };
  if (!type || !source) {
    // 非伤害（op / 治疗）：由 decideResponderAction 单独处理
    return noThreat;
  }

  // 闪电：目标有手牌 → 自动弃1不翻倍；无手牌 → 双倍
  let mult = effect.multiplier ?? 1;
  if (effect.attackExtras?.includes("闪电") && target.hand.length === 0) {
    mult *= 2;
  }

  let amount: number;
  let killable: boolean;
  if (effect.direct) {
    // 反射直结
    amount = effect.damageAmount ?? 0;
    killable = type !== "ss" && type !== "mana";
  } else {
    const res = computeDamage(
      type,
      effect.damageAmount ?? 0,
      source,
      target,
      mult,
      effect.ignoreDefense ?? 0
    );
    amount = res.finalDamage;
    killable = res.killable;
    // 暴击追加 20ss
    if (effect.attackExtras?.includes("暴击追加")) {
      amount += Math.min(20, Math.max(0, target.hp - 1));
    }
  }

  const lethal = killable && amount >= target.hp;
  const ratio = type === "mana" ? amount / Math.max(1, target.maxMp) : amount / Math.max(1, target.hp);
  const significant = ratio >= 0.3 || amount >= 15;

  // 反射后对来源的直结伤害（与 Resolver.makeReflectedEffect 口径一致）
  let reflectedWouldKillSource = false;
  if (!effect.direct && source) {
    const ref = computeDamage(
      type,
      effect.damageAmount ?? 0,
      source,
      target,
      effect.multiplier ?? 1,
      effect.ignoreDefense ?? 0
    ).finalDamage;
    reflectedWouldKillSource =
      (type !== "ss" && type !== "mana") && ref >= source.hp;
  }

  return { amount, lethal, significant, reflectedWouldKillSource };
}

// 手中可用的某类响应牌
function responseCard(
  state: GameState,
  playerId: string,
  kind: ResponseKind
): DeckCard | undefined {
  const p = state.players.find((x) => x.id === playerId)!;
  return p.hand.find((c) => c.name === kind);
}

function respond(
  state: GameState,
  playerId: string,
  kind: ResponseKind
): EngineAction | null {
  // 窗口未允许该类响应（或玩家被冻结/封印等）时不可用
  if (!canRespondWith(state, playerId, kind)) return null;
  const card = responseCard(state, playerId, kind);
  if (card) {
    return {
      type: "respond",
      playerId,
      cardUid: card.uid,
      responseKind: kind,
    };
  }
  // 替换响应（弃鬼盾→解除 / 狂风刃甲→反射）：没有真实牌时用2张手牌替换
  const player = state.players.find((p) => p.id === playerId)!;
  const sub = findSubstituteConfig(player, "respond");
  if (sub && sub.config.cardName === kind) {
    const needBlack = sub.config.suitFilter === "黑";
    const pool = needBlack
      ? player.hand.filter((c) => c.suit === "黑")
      : [...player.hand];
    if (pool.length >= 2) {
      return {
        type: "respond",
        playerId,
        cardUid: -1,
        substituteCardUids: [pool[0].uid, pool[1].uid],
        responseKind: kind,
      };
    }
  }
  return null;
}

function pass(playerId: string): EngineAction {
  return { type: "pass-response", playerId };
}

// 是否值得用【解除】抵消 op
function shouldDispel(state: GameState, playerId: string): boolean {
  const effect = state.responseWindow!.effect;
  if (effect.op?.kind !== "unequip" && effect.op?.kind !== "steal-equip") {
    return false;
  }
  const p = state.players.find((x) => x.id === playerId)!;
  const slot = effect.op.slot;
  const eq = slot ? p.equip[slot] : p.equip.weapon ?? p.equip.armor ?? p.equip.accessory;
  if (!eq) return false;
  return (
    (eq.attackBonus ?? 0) +
      (eq.defenseBonus ?? 0) +
      (eq.rangeBonus ?? 0) >=
    3
  );
}

// 定向减益状态（虚灵/无敌窗口）是否值得取消
function shouldBlockDebuff(playerId: string, statusName?: string): boolean {
  void playerId;
  if (statusName === "致命弱点") return true; // 受伤翻倍，致死前奏
  if (statusName === "无间炼狱") return true; // 整回合不能打效果牌
  if (statusName === "沉默") return true; // 不能用法术/公共法术
  return false;
}

// 响应窗口决策主入口
export function decideResponderAction(
  state: GameState,
  playerId: string
): EngineAction {
  const w = state.responseWindow;
  if (!w) return pass(playerId);
  const effect = w.effect;
  const tid = primaryTarget(effect);
  const target = state.players.find((p) => p.id === tid)!;
  const isTeammate = target.id !== playerId && target.team === state.players.find((p) => p.id === playerId)!.team;

  // 1) op 类窗口：解除装备 / 阻挡关键减益
  if (!effect.damageType) {
    if (effect.op?.kind === "unequip" || effect.op?.kind === "steal-equip") {
      if (!isTeammate && shouldDispel(state, playerId)) {
        const a = respond(state, playerId, "解除") ?? respond(state, playerId, "无敌");
        if (a) return a;
      }
      return pass(playerId);
    }
    if (effect.op?.kind === "add-status" && !isTeammate) {
      const name = effect.op.status?.name;
      if (shouldBlockDebuff(playerId, name)) {
        const a =
          (name === "致命弱点" && respond(state, playerId, "无敌")) ||
          respond(state, playerId, "虚灵");
        if (a) return a;
      }
    }
    // 补给效果（治疗）：对方队伍可出拒援阻断
    if (effect.allowResponses.includes("拒援")) {
      const source = state.players.find((p) => p.id === effect.source);
      const me = state.players.find((p) => p.id === playerId)!;
      const isOpponent = !!source && me.team !== source.team;
      if (isOpponent) {
        // 目标濒死 → 阻断救援（最高优先）
        if (target.isDying && (effect.healHp ?? 0) > 0) {
          const a = respond(state, playerId, "拒援");
          if (a) return a;
        }
        // 治疗量≥20 或占最大血量≥20% → 值得拒援
        const heal = effect.healHp ?? 0;
        if (heal >= 20 || (heal > 0 && heal / Math.max(1, target.maxHp) >= 0.2)) {
          const a = respond(state, playerId, "拒援");
          if (a) return a;
        }
      }
    }
    // 其他非伤害 op：pass
    return pass(playerId);
  }

  const threat = estimateThreat(state, effect, tid);
  const dtype = effect.damageType;

  // 2) 致死威胁
  if (threat.lethal) {
    // 队友只在能救命时出手
    // 反射可反杀来源 → 优先反杀（自己零伤+人头）
    if (!isTeammate && threat.reflectedWouldKillSource) {
      const a = respond(state, playerId, "反射");
      if (a) return a;
    }
    const a =
      respond(state, playerId, "无敌") ??
      (dtype === "w" || dtype === "hw"
        ? respond(state, playerId, "闪避")
        : respond(state, playerId, "虚灵"));
    if (a) return a;
    // 最后手段：反射（即使不反杀，自己也免伤）
    if (!isTeammate) {
      const r = respond(state, playerId, "反射");
      if (r) return r;
    }
    return pass(playerId);
  }

  // 队友：非致死不出牌（节省手牌）
  if (isTeammate) return pass(playerId);

  // 3) 显著但不致死：反射优先（以牙还牙），否则吃下保留取消牌
  if (threat.significant) {
    if (dtype !== "mana") {
      const r = respond(state, playerId, "反射");
      if (r) return r;
    }
    // ss 大额直扣且无反射：舍得用无敌
    if (dtype === "ss" && threat.amount / Math.max(1, target.hp) >= 0.5) {
      const inv = respond(state, playerId, "无敌");
      if (inv) return inv;
    }
  }

  // 4) 小额：pass
  return pass(playerId);
}

// ---------------- 拾遗窗口决策 ----------------
// 取走价值高于拾遗本身的牌（阈值 4），否则放弃
import { cardValue } from "./heuristics";
export function decidePickupAction(
  state: GameState,
  playerId: string
): EngineAction {
  const w = state.pickupWindow;
  const player = state.players.find((p) => p.id === playerId);
  if (w && player) {
    let best: { uid: number; value: number } | null = null;
    for (const c of w.cards) {
      const v = cardValue(c, player);
      if (!best || v > best.value) best = { uid: c.uid, value: v };
    }
    const pickupCard = player.hand.find(
      (c) => c.name === "拾遗" && c.kind === "效果牌"
    );
    if (best && pickupCard && best.value > 4) {
      return {
        type: "pickup",
        playerId,
        cardUid: pickupCard.uid,
        targetUid: best.uid,
      };
    }
  }
  return { type: "pass-pickup", playerId };
}
